import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorComponent, LoadingSpinner, PageLayout } from "shared";
import {
  CourseTaskSideBar,
  CourseTheory,
  CourseTaskView,
  useCourseTask,
  TaskFileUploadScope,
  CourseListingText,
  emptyTasksText
} from "..";
import { getSelectedProgram, trimText } from "utils";
import ErrorImg from "assets/images/error-robot.png";
import { MwcdLogoComopent, useAppScope } from "master";

export const CourseTask = ({ type }) => {
  let { courseId } = useParams();
  const selectedProgram = getSelectedProgram();
  const navigate = useNavigate();
  const { lanSettings } = useAppScope();

  const {
    name,
    studentTasks,
    studentTheory,
    courseTaskList,
    hasCourseGroup,
    displayName,
    selectedProgramName,
    courseGroupName,
    currentCourseTask,
    onCourseTaskCardClick,
    courseTaskDetailsQuery,
    courseType,
    updateCourseTasksStatus,
    onStartSimulation,
    setCurrentCourseTask,
    studentDataDisplayName,
    modalState,
    onOpenModal,
    onCloseModal,
    studentSpecificDataQuery,
    currentCourseTaskIndex,
    setCurrentCourseTaskIndex,
    onPrevTask,
    onNextTask,
    studentTheoryDetails,
    courseTaskDetails,
  } = useCourseTask({ load: true, courseId });

  const isAssignment = type === 2;
  const isPractical = type === 1;
  const isTheory = type === 0;
  const currentName = isAssignment
    ? `${CourseListingText.Assignment[lanSettings?.lan || "en"]}`
    : isTheory
      ? `${CourseListingText.LearningMaterials[lanSettings?.lan || "en"]}`
      : `${CourseListingText.LearningContent[lanSettings?.lan || "en"]}`;

  const hasCourseTask = studentTasks?.length > 0;
  const hasCourseTheory = studentTheory?.length > 0;
  const isLastTask = studentTasks?.length - 1 === currentCourseTaskIndex;
  const { isPending } = courseTaskDetailsQuery;
  const {
    title,
    taskGroup,
    taskContents,
    taskSections,
    description,
    startDate,
    endDate,
    initialHolding,
    maxAttempt,
    remainingAttempts,
    completionPercentage,
  } = !!courseTaskDetails && courseTaskDetails;

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    sessionStorage.setItem("courseId", JSON.stringify(courseId));
  }, []);

  useEffect(() => {
    if (
      (hasCourseTask && currentCourseTask === null) ||
      (hasCourseTask &&
        !studentTasks?.some(
          (e) => e?.courseTaskId === currentCourseTask?.courseTaskId
        ))
    ) {
      onCourseTaskCardClick(studentTasks[0]);
      setCurrentCourseTaskIndex(0);
      sessionStorage.setItem("currentTask", JSON.stringify(studentTasks[0]));
    } else {
      const newCurrent = studentTasks?.find(
        (e) => e?.courseTaskId === currentCourseTask?.courseTaskId
      );
      const newCurrentIndex = studentTasks?.findIndex(
        (e) => e?.courseTaskId === currentCourseTask?.courseTaskId
      );
      if (newCurrent) {
        setCurrentCourseTask(newCurrent);
        setCurrentCourseTaskIndex(newCurrentIndex);
      }
    }
  }, [studentTasks]);

  const onCourseCardNavigate = (element) => {
    onCourseTaskCardClick(element);
    navigate(`view`);
  };

  if (courseTaskList?.isPending || courseTaskList?.isLoading) {
    return <LoadingSpinner />;
  }
  const breadcrumb = [
    { label: selectedProgramName, location: "/program" },
    {
      label: displayName ? displayName : currentName,
      location: isAssignment
        ? `/program/assignment/${hasCourseGroup ? "coursegroup" : "courseslist"
        }`
        : isTheory
          ? `/program/learningmaterials/${hasCourseGroup ? "coursegroup" : "courseslist"
          }`
          : `/program/practicals/${hasCourseGroup ? "coursegroup" : "courseslist"
          }`,
    },
    {
      label: hasCourseGroup ? courseGroupName : trimText(name),
      location: hasCourseGroup
        ? isAssignment
          ? "/program/assignment/courseslist"
          : isTheory
            ? "/program/learningmaterials/courseslist"
            : "/program/practicals/courseslist"
        : "",
    },
  ];
  const goToPreviousPage = () => {
    navigate(-1, { replace: true });
  };

  if (hasCourseGroup) {
    breadcrumb.push({ label: name });
  }
  const titleEndComponent = [
    <div className="logo-group">
      <div className="logo-group"><MwcdLogoComopent customClass="logo-group-mwcd_PD" /></div>
    </div>,
  ];

  return (
    <TaskFileUploadScope>
      <PageLayout
        isBreadCrumbsMarginRequired={false}
        isFullWidthContainer={false}
        isRowRequired={false}
        breadcrumb={breadcrumb}
        isBreadCrumbsPaddingRequired={screenWidth > 767}
        titleEndComponent={hasCourseTheory || hasCourseTask ? null : titleEndComponent}
        isPageEmpty={!hasCourseTheory && !hasCourseTask}
      >
        <div className="single-page-content">
          {hasCourseTheory || hasCourseTask ? (
            <>
              <div className="container position-relative">
                <div className="row course-task-row">
                  {(isPractical || isAssignment) && hasCourseTask && (
                    <>
                      <CourseTaskSideBar
                        name={name}
                        studentTasks={studentTasks}
                        currentCourseTask={currentCourseTask}
                        onCourseTaskCardClick={onCourseTaskCardClick}
                        onCourseCardNavigate={onCourseCardNavigate}
                        isMobile={screenWidth < 768}
                        studentDataDisplayName={studentDataDisplayName}
                        modalState={modalState}
                        onOpenModal={onOpenModal}
                        onCloseModal={onCloseModal}
                        studentSpecificDataQuery={studentSpecificDataQuery}
                        courseType={courseType}
                        displayDuration={selectedProgram?.displayDuration}
                      />
                      {screenWidth > 767 && (
                        <CourseTaskView
                          title={title}
                          isPending={isPending}
                          description={description}
                          taskGroup={taskGroup}
                          taskContents={taskContents}
                          taskSections={taskSections}
                          courseType={courseType}
                          currentCourseTask={currentCourseTask}
                          onStartSimulation={onStartSimulation}
                          updateCourseTasksStatus={updateCourseTasksStatus}
                          isMobile={screenWidth < 768}
                          currentCourseTaskIndex={currentCourseTaskIndex}
                          onCourseTaskCardClick={onCourseTaskCardClick}
                          onPrevTask={onPrevTask}
                          onNextTask={onNextTask}
                          isLastTask={isLastTask}
                          startDate={startDate}
                          initialHolding={initialHolding}
                          endDate={endDate}
                          modalState={modalState}
                          onCloseModal={onCloseModal}
                          onOpenModal={onOpenModal}
                          maxAttempt={maxAttempt}
                          remainingAttempts={remainingAttempts}
                          completionPercentage={completionPercentage}
                        />
                      )}
                    </>
                  )}
                  {isTheory && hasCourseTheory && (
                    <CourseTheory
                      studentTheory={studentTheory}
                      studentTheoryDetails={studentTheoryDetails}
                      modalState={modalState}
                      onOpenModal={onOpenModal}
                      onCloseModal={onCloseModal}
                      studentSpecificDataQuery={studentSpecificDataQuery}
                    />
                  )}
                </div>
                <MwcdLogoComopent />
              </div>
            </>
          ) : (
            <ErrorComponent
              title={emptyTasksText.NoTasks[lanSettings?.lan || "en"]}
              message={emptyTasksText.Message[lanSettings?.lan || "en"]}
              image={ErrorImg}
              btnLabel={emptyTasksText.GoBack[lanSettings?.lan || "en"]}
              onClick={goToPreviousPage}
            />
          )}
        </div>
      </PageLayout>
    </TaskFileUploadScope>
  );
};
